import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash";
import { useHistory } from "react-router-dom";
import { encode } from "Helpers/index";
import { DataTable, Button } from "@life-line-lab/components-react";
import DataTableFilter from "Components/shared/data-table-filter";
import { BasicModal } from "@life-line-lab/components-react";
import useSearch from "Hooks/search.hook";
import SetHandoverPointModal from "Components/delivery-management/parcel-tracking-modals/sorting-modal/set-handover-point";
import useSession from "Hooks/session.hook";
import { getStatusStyle } from "Helpers/get-status-colours";
import { toCapitalisedSpaced } from "Helpers/to-capitalised-spaced";
import Tooltip from "antd/lib/tooltip";
import QuestionCircleOutlined from "@ant-design/icons/lib/icons/QuestionCircleOutlined";

export interface Option {
  label: string;
  value: string;
}

const OptionButtons = (props) => {
  const { selectedRows, handleOpenSetHandover } = props || {};

  const { t } = useTranslation();
  return (
    <div className="dm-buttons-container">
      <div className="dm-action-btn-container alignment">
        <Button
          type="default"
          isDisabled={isEmpty(selectedRows) || selectedRows.length > 1}
          label={t("action.setHandoverPoint")}
          size="large"
          onClick={handleOpenSetHandover}
        />
        <Tooltip title={t("txt.setHandoverPointGuide")} placement="topLeft">
          <QuestionCircleOutlined className="dm-action-btn-container tool-tip" />
        </Tooltip>
      </div>
    </div>
  );
};

interface SortingBatchesListProps {
  onFilter?: any;
  customerOptions?: any;
  sortingHubOptions?: any;
  allOrderList?: any;
  onSetHandoverPoint: any;
}

const SortingBatchesList: React.FC<SortingBatchesListProps> = ({
  customerOptions,
  sortingHubOptions,
  allOrderList,
  onFilter,
  onSetHandoverPoint,
}) => {
  const [callModal, setCallModal] = useState<any>(<></>);
  const { roleTypes } = useSession();
  const { t } = useTranslation();
  const history = useHistory();
  const initialModal: any = {
    open: false,
  };
  const [modal, setModal] = useState<any>(initialModal);
  const [selectedRows, setSelectedRows] = useState<any>([]);

  const initialFilter: any = {
    customerOrgList: "",
    sortingHub: "",
  };

  const [filterState, setFilterState] = useState<{
    [key: string]: string | boolean;
  }>(initialFilter);

  const filterSelection = [
    {
      key: "customerOrgList",
      name: "customerOrgList",
      label: t("placeholder.filterCustomer"),
      placeholder: t("placeholder.filterCustomer"),
      value: filterState?.customerOrgList,
      options: [{ label: t("option.all"), value: "" }].concat(
        customerOptions?.map((org) => {
          return {
            label: org.name || "",
            value: org._id,
          };
        }),
      ),
    },
    {
      key: "sortingHub",
      name: "sortingHub",
      label: t("placeholder.byOriginHub"),
      placeholder: t("placeholder.byOriginHub"),
      value: filterState?.sortingHub,
      options: [{ label: t("option.all"), value: "" }].concat(
        sortingHubOptions?.map((hub) => {
          return {
            label: hub.locationName || "",
            value: hub._id,
          };
        }),
      ),
    },
  ];

  const { handleSearch } = useSearch({ onFilter, filterState, setFilterState });

  const rowSelection = useMemo(
    () =>
      roleTypes !== "CUSTOMER-SUPER_ADMIN"
        ? {
            selectedRowKeys: selectedRows,
            getCheckboxProps: (record) => {
              const allowedStatus = [
                "OPEN",
                "DROP_OFF_IN_PROGRESS",
                "DROPPED_OFF",
                "PICKED_UP",
                "PICKUP_IN_PROGRESS",
              ];

              const isOrderValid = record.orders?.every((order: any) =>
                order
                  ? allowedStatus.includes(order.orderStatusInfo.orderStatus)
                  : false,
              );

              return {
                disabled: !isOrderValid,
              };
            },
            onChange: (newSelectedRows: any[]) => {
              setSelectedRows(newSelectedRows);
            },
          }
        : undefined,
    [allOrderList, selectedRows, setSelectedRows],
  );

  const handleResetModalState = () => {
    setModal(initialModal);
  };

  const handleFilterSelection = (selection) => {
    setFilterState((prevFilter) => ({
      ...prevFilter,
      customerOrgList: selection.customerOrgList,
      sortingHub: selection.sortingHub,
    }));
  };

  const handleSetHandoverPoint = (payload) => {
    onSetHandoverPoint(payload);
    handleResetModalState();
  };

  const handleOpenSetHandover = () => {
    const selectedBatchID = selectedRows[0];
    const batchCode = allOrderList.find(
      (batch: any) => batch._id === selectedBatchID,
    ).batchCode;

    const batchDetails = {
      id: selectedBatchID,
      code: batchCode,
    };

    setCallModal(
      <SetHandoverPointModal
        batchDetails={batchDetails}
        handoverOptions={sortingHubOptions}
        handleCancel={handleResetModalState}
        onSubmit={handleSetHandoverPoint}
        setSelectedRows={setSelectedRows}
      />,
    );
    setModal({ open: true, type: "CREATE" });
  };

  const handleOpenBatchesView = (batchesID) => {
    let encodedId = encode(batchesID);
    history.push(
      `/delivery-management/parcel-tracking/batches/${encodedId}/view`,
    );
  };

  const columns: any = [
    {
      title: t("label.originHub").toLocaleUpperCase(),
      dataIndex: "sortingHub",
      width: 150,
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          <div className="align-flex-end ">
            <span
              className="half-square custom-icon mb-5"
              style={{
                borderLeft: `12px solid transparent`,
                borderBottom: `12px solid #E8A51A`,
              }}
            >
              &nbsp;
            </span>
            <span>&nbsp;{record.sortingHubData?.name || ""}</span>
          </div>
        );
      },
    },
    {
      title: t("label.batchNo").toLocaleUpperCase(),
      dataIndex: "batchCode",
      width: 150,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <a className="clickable-id">{record.batchCode || ""}</a>;
      },
      onCell: (record, rowIndex) => {
        return {
          onClick: (e: React.MouseEvent<HTMLElement>) => {
            handleOpenBatchesView(record._id);
          },
        };
      },
    },
    {
      title: t("label.noOfParcels").toLocaleUpperCase(),
      dataIndex: "noOfParcels",
      width: 100,
      ellipsis: true,
    },
    {
      title: t("label.weightInKg").toLocaleUpperCase(),
      dataIndex: "weight",
      width: 105,
      ellipsis: true,
      render: (_: any, record: any) => {
        // it is possible to have multiple weights
        // -> show the sum of all weights
        if (isEmpty(record.orders)) return "-";

        const totalWeights = record.orders.reduce(
          (acc, curr) => (curr?.weight ? acc + parseFloat(curr.weight) : acc),
          0,
        );

        return <span>{totalWeights.toFixed(2)}</span>;
      },
    },
    {
      title: t("label.cubicVolume").toLocaleUpperCase(),
      dataIndex: "volume",
      width: 105,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{parseInt(record.totalVolume).toLocaleString()}</span>;
      },
    },
    {
      title: t("label.handoverPoint").toLocaleUpperCase(),
      dataIndex: ["handoverPointData", "name"],
      width: 105,
      ellipsis: true,
    },
    {
      title: t("label.status").toLocaleUpperCase(),
      dataIndex: "status",
      width: 100,
      ellipsis: true,
      render: (status: any) => {
        return (
          <span className="status-indicator" style={getStatusStyle(status)}>
            {status ? toCapitalisedSpaced(status) : "-"}
          </span>
        );
      },
    },
  ];

  return (
    <>
      <div className="parcel-order-list">
        <DataTable
          rowKey={(record: any) => {
            return record._id;
          }}
          rowSelection={rowSelection}
          columns={columns}
          dataSource={allOrderList || []}
          title={() => (
            <div>
              <div className="filter-component">
                <DataTableFilter
                  searchPlaceholder={t("placeholder.searchByBatchNo")}
                  onSearch={(text: string) => {
                    handleSearch(text);
                  }}
                  onFilter={(selection) => handleFilterSelection(selection)}
                  renderFilterSelection={filterSelection}
                />
                {roleTypes == "COMPANY-SUPER_ADMIN" && (
                  <OptionButtons
                    selectedRows={selectedRows}
                    handleOpenSetHandover={handleOpenSetHandover}
                  />
                )}
              </div>
              <div>
                <h4>Total Batches: {allOrderList.length}</h4>
              </div>
            </div>
          )}
        />
      </div>

      <BasicModal
        className="modal"
        isOpen={modal.open}
        isClosable={true}
        isMaskClosable={false}
        onClose={() => {
          handleResetModalState();
        }}
      >
        {callModal}
      </BasicModal>
    </>
  );
};

export default SortingBatchesList;
